import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export interface TabItem {
  key: string; // = pathname (unique per open page)
  path: string; // the URL to navigate to for this tab
  title: string;
}

interface TabsState {
  tabs: TabItem[];
  activeKey: string;
  /** Open a tab for `path` (or focus it if already open) and make it active. */
  openTab: (path: string, title: string) => void;
  setActive: (key: string) => void;
  removeTab: (key: string) => void;
  closeOthers: (key: string) => void;
  closeToRight: (key: string) => void;
  closeAll: () => void;
  updateTitle: (key: string, title: string) => void;
}

export const useTabsStore = create<TabsState>()(
  persist(
    (set) => ({
      tabs: [],
      activeKey: "",

      openTab: (path, title) =>
        set((state) => {
          const existing = state.tabs.find((t) => t.key === path);
          if (existing) {
            // Keep the first meaningful title; don't overwrite a page-set title
            // with a generic default.
            return { activeKey: path };
          }
          return {
            tabs: [...state.tabs, { key: path, path, title }],
            activeKey: path,
          };
        }),

      setActive: (key) => set({ activeKey: key }),

      removeTab: (key) =>
        set((state) => ({ tabs: state.tabs.filter((t) => t.key !== key) })),

      closeOthers: (key) =>
        set((state) => ({ tabs: state.tabs.filter((t) => t.key === key) })),

      closeToRight: (key) =>
        set((state) => {
          const idx = state.tabs.findIndex((t) => t.key === key);
          if (idx === -1) return state;
          return { tabs: state.tabs.slice(0, idx + 1) };
        }),

      closeAll: () => set({ tabs: [] }),

      updateTitle: (key, title) =>
        set((state) => ({
          tabs: state.tabs.map((t) =>
            t.key === key ? { ...t, title } : t
          ),
        })),
    }),
    {
      name: "app-tabs",
      storage: createJSONStorage(() => sessionStorage),
    }
  )
);
